import { useState, useMemo, useCallback, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";
import type { ColumnSort, OnChangeFn, SortingState } from "@tanstack/react-table";
import Table, { TableHeading, TableControls, TableFooter } from "@/components/Table";
import NotesTable from "@/features/global/components/NotesTable";
import EmptyState from "@/components/EmptyState";
import SearchInput from "@/components/SearchInput";
import Select from "@/components/Select";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import usePagination from "@/hooks/usePagination";
import { useSearchWithParams, useParams as useUrlParams } from "@/hooks/useSearchParams";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { useUniversityStaffPermissions } from "@/hooks/useUniversityStaffPermissions";
import { GET_UNIVERSITY_PROBATIONS_QUERY } from "./queries";
import { buildColumns, mapProbations } from "./_utils";
import { Probation } from "./types";
import ProbationModal from "./ProbationModal";
import { UNIVERSITY_QUERY } from "../../queries";
import AddEditNoteSideSheet from "@/features/global/components/AddEditNoteSideSheet";
import { NoteNotableValues } from "@/types";
import useCapabilities from "@/hooks/useCapabilities";

const STATUS_ALL = "ALL";
const STATUS_OPTIONS = [
  { value: STATUS_ALL, label: "All Statuses" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" }
] as const;

const DEFAULT_SORT: ColumnSort[] = [{ id: "ACTIVE", desc: true }];

type ProbationStatusFilter = "ACTIVE" | "INACTIVE";

export default function ProbationsPage() {
  const { universityId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isCompactLayout } = useIsCompactLayout();
  const { Menu, setMenu } = useTableRowMenu();
  const { shouldShowPersonProfileActions } = useUniversityStaffPermissions();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { params, addSearchParam, deleteSearchParams } = useUrlParams();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });

  const statusFilter = useMemo(() => {
    const raw = typeof params.status === "string" ? params.status : undefined;
    if (raw === "ACTIVE" || raw === "INACTIVE") return raw;
    return undefined;
  }, [params.status]);

  const [sortingState, setSortingState] = useState<SortingState>(DEFAULT_SORT);
  const [selectedStudent, setSelectedStudent] = useState<Probation | null>(null);
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    mode: "create" | "end" | "edit";
    probation: Probation | null;
  }>({
    isOpen: false,
    mode: "create",
    probation: null
  });

  useEffect(() => {
    resetPagination();
  }, [debouncedSearch, statusFilter, resetPagination]);

  const orderBy = useMemo(() => {
    const first = sortingState[0];
    if (!first || first.id !== "ACTIVE") return undefined;
    return {
      field: "ACTIVE" as const,
      direction: (first.desc ? "DESC" : "ASC") as "ASC" | "DESC"
    };
  }, [sortingState]);

  const handleSortChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      setSortingState((prev) => {
        const next = typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue;
        return next.length > 0 ? next : DEFAULT_SORT;
      });
      resetPagination();
    },
    [resetPagination]
  );

  const { data: universityData } = useQuery(UNIVERSITY_QUERY, {
    variables: { legacyId: universityId },
    skip: !universityId
  });

  // Get the actual university ID from the university query
  const actualUniversityId = universityData?.universities[0]?.id;

  const { data, loading, refetch } = useQuery(GET_UNIVERSITY_PROBATIONS_QUERY, {
    variables: {
      universityId: actualUniversityId || "",
      ...paginationVariables,
      search: debouncedSearch || undefined,
      status: statusFilter,
      orderBy
    },
    skip: !actualUniversityId,
    fetchPolicy: "cache-first"
  });

  const probationsConnection = useMemo(() => {
    const connection = data?.university?.probations;
    if (!connection) return undefined;

    return {
      ...connection,
      pageInfo: {
        ...connection.pageInfo,
        startCursor: connection.pageInfo.startCursor ?? undefined,
        endCursor: connection.pageInfo.endCursor ?? undefined
      }
    };
  }, [data]);
  const probations = useMemo(() => mapProbations(probationsConnection?.nodes || []), [probationsConnection]);

  const hasActiveFilters = !!debouncedSearch || !!statusFilter;

  const handleStatusChange = (value: string) => {
    if (value === STATUS_ALL) {
      deleteSearchParams("status");
    } else {
      addSearchParam("status", value as ProbationStatusFilter);
    }
    resetPagination();
  };

  const handleClearAllFilters = () => {
    clearSearch();
    deleteSearchParams("status");
    resetPagination();
  };

  const handleViewStudent = useCallback((probation: Probation) => {
    setSelectedStudent(probation);
    setIsDetailsPanelOpen(true);
  }, []);

  const handleOpenCreateModal = () => {
    setModalState({ isOpen: true, mode: "create", probation: null });
  };

  const handleOpenEndModal = useCallback((probation: Probation) => {
    setModalState({ isOpen: true, mode: "end", probation });
  }, []);

  const handleOpenEditModal = useCallback((probation: Probation) => {
    setModalState({ isOpen: true, mode: "edit", probation });
  }, []);

  const handleCloseModal = () => {
    setModalState({ isOpen: false, mode: "create", probation: null });
  };

  const { id: currentUserId } = useCapabilities();
  const [addNoteProbationId, setAddNoteProbationId] = useState<string | null>(null);
  const [addNotePgaId, setAddNotePgaId] = useState<string | undefined>(undefined);

  const handleOpenAddNote = useCallback((probation: Probation) => {
    setAddNoteProbationId(probation.id);
    setAddNotePgaId(probation.person.pgaId);
  }, []);

  const handleCloseAddNote = useCallback(() => {
    setAddNoteProbationId(null);
    setAddNotePgaId(undefined);
  }, []);

  const handleAddNoteSuccess = useCallback(() => {
    refetch();
    setAddNoteProbationId(null);
    setAddNotePgaId(undefined);
  }, [refetch]);

  const columns = useMemo(
    () =>
      buildColumns({
        isCompactLayout,
        navigate,
        location,
        universityId: universityId || "",
        handleViewStudent,
        setMenu,
        actionId,
        handleOpenEndProbationDialog: handleOpenEndModal,
        handleOpenEditProbationTypeDialog: handleOpenEditModal,
        handleOpenAddNote
      }),
    [
      isCompactLayout,
      navigate,
      location,
      universityId,
      handleViewStudent,
      setMenu,
      handleOpenEndModal,
      handleOpenAddNote,
      handleOpenEditModal
    ]
  );

  return (
    <>
      <TableHeading
        heading="PGM Program Probations"
        body="Search for or view all current students associated with this PGM University Program that have been placed on probation."
        actions={[
          {
            label: "Add Probation",
            icon: "add",
            onClick: handleOpenCreateModal
          }
        ]}
      />
      <Table
        data={probations}
        loading={loading}
        columns={columns}
        data-testid="probations-table"
        hasActiveFilters={hasActiveFilters}
        sortingState={sortingState}
        onSortingChange={handleSortChange}
        serverSideSorting={true}
        onClickRow={(row) => {
          const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
          navigate(
            `/education/universities/${universityId}/students/${row.original.person.pgaId}?returnUrl=${returnUrl}`
          );
        }}
        renderExpandedRow={({ row }) => (
          <NotesTable
            variant="details-panel"
            data={row.original.notes}
            title={`Notes (${row.original.notes.length})`}
            noteEditing={{
              enabled: true,
              notableId: row.original.id,
              notableType: NoteNotableValues.UNIVERSITY_PROBATION,
              refetchQueries: [GET_UNIVERSITY_PROBATIONS_QUERY],
              pgaId: row.original.person.pgaId
            }}
          />
        )}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Probations" caption="No students are currently on probation." iconName="person_off" />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState
            title="No Matching Probations"
            caption="Try a different search or status filter."
            iconName="search"
          />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            pagination={createHeaderPagination(probationsConnection, { loading })}
            onSortClick={onSortClick}
            useExpandedFilters
            expandedFiltersState={{ status: !!statusFilter }}
            activeFilters={statusFilter ? 1 : 0}
            onClearAllFilters={handleClearAllFilters}
          >
            <SearchInput
              placeholder="Search Students"
              query={searchValue}
              onChange={(value) => {
                setSearchValue(value);
                resetPagination();
              }}
              onClear={() => {
                clearSearch();
                resetPagination();
              }}
            />
            <div data-name="status">
              <Select
                label="Status"
                value={statusFilter || STATUS_ALL}
                options={STATUS_OPTIONS}
                onSelect={handleStatusChange}
              />
            </div>
          </TableControls>
        )}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(probationsConnection, { loading })}
            totalRowCount={probationsConnection?.totalCount ?? probations.length}
          />
        )}
      />
      <Menu />

      <ProbationModal
        isOpen={modalState.isOpen && !!actualUniversityId}
        onClose={handleCloseModal}
        onSuccess={() => {
          refetch();
          handleCloseModal();
        }}
        universityLegacyId={universityData?.universities?.[0]?.legacyId || universityId || ""}
        mode={modalState.mode}
        probation={modalState.probation}
        selectedStudent={null}
      />

      <PersonSideSheet
        open={isDetailsPanelOpen && !!selectedStudent}
        onClose={() => setIsDetailsPanelOpen(false)}
        pgaId={selectedStudent?.person.pgaId ?? null}
        title="Student Details"
        showPersonalDetails={true}
        universityName={universityData?.universities[0]?.name}
        customActionButtons={
          selectedStudent
            ? [
                {
                  text: "View PGM Details",
                  variant: "outlined" as const,
                  onClick: () => {
                    const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
                    navigate(
                      `/education/universities/${universityId}/students/${selectedStudent.person.pgaId}?returnUrl=${returnUrl}`
                    );
                  }
                },
                ...(shouldShowPersonProfileActions
                  ? [
                      {
                        text: "View Full Profile",
                        variant: "text" as const,
                        onClick: () => {
                          window.open(`/people/${selectedStudent.person.pgaId}/person/overview`, "_blank");
                        }
                      }
                    ]
                  : [])
              ]
            : undefined
        }
      />

      {addNoteProbationId && (
        <AddEditNoteSideSheet
          open={!!addNoteProbationId}
          onClose={handleCloseAddNote}
          notableId={addNoteProbationId}
          notableType={NoteNotableValues.UNIVERSITY_PROBATION}
          refetchQueries={[GET_UNIVERSITY_PROBATIONS_QUERY]}
          currentUserId={currentUserId || undefined}
          pgaId={addNotePgaId}
          objectType="student"
          onSaveSuccess={handleAddNoteSuccess}
        />
      )}
    </>
  );
}
